import { useEffect, useState } from "react";
import { MapContainer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { Navigation, ExternalLink, LocateFixed, Search, Car, Footprints, Loader2, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BaseTiles, pinIcon, ParcelShape } from "@/components/LocationPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const googleDirectionsUrl = (lat, lng, mode = "driving") =>
  `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=${mode}`;

const fmtKm = (m) => (m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1).replace(".", ",")} km`);
const fmtDur = (s) => { const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60); return h ? `${h} sa ${m} dk` : `${Math.max(m, 1)} dk`; };

const FitRoute = ({ coords }) => {
  const map = useMap();
  useEffect(() => { if (coords.length) map.fitBounds(L.latLngBounds(coords), { padding: [30, 30] }); }, [coords]);
  return null;
};

const StartPicker = ({ start, onStart }) => {
  const { user, consents, refresh } = useAuth();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  const [askConsent, setAskConsent] = useState(false);
  const locate = async (granting) => {
    if (user.office_id && !consents.IZIN_KONUM && !granting) return setAskConsent(true);
    if (granting) {
      try { await api.post("/legal/consents/IZIN_KONUM", { granted: true }); await refresh(); } catch (x) { return setErr(formatApiError(x.response?.data?.detail)); }
      setAskConsent(false);
    }
    if (!navigator.geolocation) return setErr("Tarayıcınız konum özelliğini desteklemiyor. Adres yazın.");
    setBusy("gps"); setErr("");
    navigator.geolocation.getCurrentPosition(
      (p) => { setBusy(null); onStart({ lat: p.coords.latitude, lng: p.coords.longitude, label: "Konumum" }); },
      () => { setBusy(null); setErr("Konum izni verilmedi. Başlangıç adresini yazabilirsiniz."); },
      { enableHighAccuracy: true, timeout: 10000 });
  };
  const search = async (e) => {
    e.preventDefault();
    setBusy("q"); setErr("");
    try { const { data } = await api.get("/geocode", { params: { q } }); setHits(data); if (!data.length) setErr("Adres bulunamadı."); }
    catch (x) { setErr(formatApiError(x.response?.data?.detail)); }
    setBusy(null);
  };
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Başlangıç noktası</div>
      <div className="flex gap-2 flex-wrap">
        <Button type="button" variant="outline" size="sm" data-testid="route-use-location" onClick={() => locate()} disabled={busy === "gps"}>
          {busy === "gps" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <LocateFixed className="w-4 h-4 mr-1.5" />}Konumumu kullan
        </Button>
        <form onSubmit={search} className="flex gap-2 flex-1 min-w-[220px]">
          <Input data-testid="route-start-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="veya başlangıç adresi yazın" minLength={3} required />
          <Button type="submit" size="sm" variant="outline" data-testid="route-start-search" disabled={busy === "q"}><Search className="w-4 h-4" /></Button>
        </form>
      </div>
      {askConsent && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-2" data-testid="route-location-consent">
          <div>Konumunuzu kullanmak için Konum Bilgisi Kullanım İzni gerekir. Konum yalnızca bu rota için alınır, saklanmaz. İzni Sözleşmelerim ve Onaylarım ekranından geri alabilirsiniz.</div>
          <div className="flex gap-2">
            <Button type="button" size="sm" className="bg-emerald-700 hover:bg-emerald-800" onClick={() => locate(true)} data-testid="route-consent-grant">İzin ver ve devam et</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setAskConsent(false)} data-testid="route-consent-cancel">Adres yazacağım</Button>
          </div>
        </div>
      )}
      {hits.length > 0 && (
        <div className="max-h-32 overflow-y-auto rounded-lg border border-slate-200 divide-y" data-testid="route-start-results">
          {hits.map((h, i) => (
            <button key={i} type="button" data-testid={`route-start-hit-${i}`} onClick={() => { onStart(h); setHits([]); }} className="w-full text-left text-xs px-3 py-2 hover:bg-emerald-50">{h.label}</button>
          ))}
        </div>
      )}
      {start && <div className="text-xs text-emerald-800" data-testid="route-start-selected">Başlangıç: {start.label}</div>}
      {err && <div className="text-xs text-amber-700" data-testid="route-start-error">{err}</div>}
    </div>
  );
};

export function DirectionsDialog({ open, onClose, target }) {
  const [start, setStart] = useState(null);
  const [mode, setMode] = useState("car");
  const [route, setRoute] = useState(null);
  const [state, setState] = useState({ busy: false, err: "" });

  useEffect(() => { if (!open) { setStart(null); setRoute(null); setState({ busy: false, err: "" }); } }, [open]);
  useEffect(() => {
    if (!start || !target) return;
    setState({ busy: true, err: "" });
    api.get("/route", { params: { from_lat: start.lat, from_lng: start.lng, to_lat: target.lat, to_lng: target.lng, mode } })
      .then(({ data }) => { setRoute(data); setState({ busy: false, err: "" }); })
      .catch((x) => { setRoute(null); setState({ busy: false, err: formatApiError(x.response?.data?.detail) }); });
  }, [start, mode, target?.lat, target?.lng]);

  if (!target) return null;
  const coords = route ? route.geometry.coordinates.map(([x, y]) => [y, x]) : [];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="directions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Navigation className="w-5 h-5 text-emerald-700" />Yol Tarifi</DialogTitle>
          <DialogDescription>{target.label}</DialogDescription>
        </DialogHeader>
        <Button data-testid="route-google-btn" className="w-full bg-emerald-700 hover:bg-emerald-800" onClick={() => window.open(googleDirectionsUrl(target.lat, target.lng, mode === "foot" ? "walking" : "driving"), "_blank", "noopener")}>
          <ExternalLink className="w-4 h-4 mr-2" />Google Haritalar'da aç
        </Button>
        <div className="rounded-xl border border-slate-200 p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="font-semibold text-slate-900 text-sm">EmlakPlus içinde rota</div>
            <div className="flex rounded-full border border-slate-200 p-0.5" data-testid="route-mode-toggle">
              {[["car", Car, "Araç"], ["foot", Footprints, "Yürüyüş"]].map(([k, I, l]) => (
                <button key={k} type="button" data-testid={`route-mode-${k}`} onClick={() => setMode(k)}
                  className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 transition-colors ${mode === k ? "bg-emerald-700 text-white" : "text-slate-600 hover:bg-slate-100"}`}><I className="w-3.5 h-3.5" />{l}</button>
              ))}
            </div>
          </div>
          <StartPicker start={start} onStart={setStart} />
          {state.busy && <div className="text-xs text-slate-500 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" />Rota hesaplanıyor…</div>}
          {state.err && <div className="text-xs text-red-700 flex items-center gap-1.5" data-testid="route-error"><AlertTriangle className="w-3.5 h-3.5" />{state.err}</div>}
          {route && (
            <div className="grid grid-cols-2 gap-2" data-testid="route-summary">
              <div className="rounded-lg bg-emerald-50 p-3"><div className="text-[10px] uppercase tracking-wider text-emerald-800">Mesafe</div><div className="font-bold text-emerald-900" data-testid="route-distance">{fmtKm(route.distance_m)}</div></div>
              <div className="rounded-lg bg-emerald-50 p-3"><div className="text-[10px] uppercase tracking-wider text-emerald-800">Tahmini süre</div><div className="font-bold text-emerald-900" data-testid="route-duration">{fmtDur(route.duration_s)}</div></div>
            </div>
          )}
          <div className="h-72 rounded-lg overflow-hidden border border-slate-200 relative z-0">
            <MapContainer center={[target.lat, target.lng]} zoom={15} className="h-full w-full">
              <BaseTiles />
              <ParcelShape geometry={target.geometry} fit={!route} />
              <Marker position={[target.lat, target.lng]} icon={pinIcon()} />
              {start && <Marker position={[start.lat, start.lng]} icon={pinIcon("#2563EB")} />}
              {coords.length > 0 && <><Polyline positions={coords} pathOptions={{ color: "#2563EB", weight: 5, opacity: 0.8 }} /><FitRoute coords={coords} /></>}
            </MapContainer>
          </div>
          <div className="text-[11px] text-slate-400">Rota verisi: OpenStreetMap katkıcıları · süreler tahminidir.</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DirectionsButton({ target, testid = "directions-btn", size = "default", variant = "outline", className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" size={size} variant={variant} className={className} data-testid={testid} onClick={() => setOpen(true)}><Navigation className="w-4 h-4 mr-2" />Yol Tarifi</Button>
      <DirectionsDialog open={open} onClose={() => setOpen(false)} target={target} />
    </>
  );
}
